import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styles from './BrandEmailCalendarPage.module.css';
import DataFallback from '../components/feedback/DataFallback';

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function BrandEmailCalendarPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const userType = useSelector(state => state.user.user_type);
  const userId = useSelector(state => state.user.user_id);
  
  console.log('BrandEmailCalendarPage loaded with brandId:', brandId);
  console.log('BrandEmailCalendarPage - userType:', userType, 'userId:', userId);
  
  // Determine if this is a brand user (they should see list view with only approved emails)
  const isBrandUser = userType === 'appuser';
  
  const today = new Date();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [emailReports, setEmailReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReportHtml, setSelectedReportHtml] = useState(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');

  // Fetch email reports from API
  useEffect(() => {
    const fetchEmailReports = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, using demo data');
        // Use demo data when no token
        const demoReports = [
          {
            id: 1,
            brand_id: brandId || '1',
            subject: 'Weekly Performance Report',
            sent_date: '2025-06-15',
            status: 'pending_review',
            html_content: '<h1>Demo Email Report</h1><p>This is a demo email report for testing purposes.</p>'
          },
          {
            id: 2,
            brand_id: brandId || '1', 
            subject: 'Monthly Campaign Summary',
            sent_date: '2025-06-01',
            status: 'approved',
            html_content: '<h1>Monthly Summary</h1><p>Your monthly campaign performance summary.</p>'
          },
          {
            id: 3,
            brand_id: brandId || '1', 
            subject: 'Product Performance',
            sent_date: '2025-05-20',
            status: 'rejected',
            html_content: '<h1>Product Performance</h1><p>Your product performance report.</p>'
          }
        ];
        setEmailReports(demoReports);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/email-reports/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
        }

        const data = await response.json();
        console.log('Email reports API response:', data);
        
        // Filter reports for the current brand if brandId is available
        const reports = data.reports || [];
        const filteredReports = brandId 
          ? reports.filter(report => String(report.brand_id) === String(brandId))
          : reports;
        
        setEmailReports(filteredReports);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching email reports:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchEmailReports();
  }, [brandId]);

  // Fetch individual report HTML when event is clicked
  const fetchReportHtml = async (reportId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setHtmlLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/email-reports/${reportId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
      }

      const data = await response.json();
      console.log('Report details API response:', data);
      setSelectedReportHtml(data.html_content);
      setHtmlLoading(false);
    } catch (err) {
      console.error('Error fetching report HTML:', err);
      setHtmlLoading(false);
    }
  };

  // Handle approval/disapproval of pending reports
  const handleApproval = async (reportId, action) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setApprovalLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/email-reports/${reportId}/approve/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: action,
          notes: approvalNotes || (action === 'approve' ? 'Approved via calendar interface' : 'Needs changes before approval')
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} report`);
      }

      const data = await response.json();
      console.log(`Report ${action} response:`, data);
      
      // Update the report status in local state
      setEmailReports(prevReports => 
        prevReports.map(report => 
          report.id === reportId 
            ? { ...report, status: action === 'approve' ? 'approved' : 'rejected' }
            : report
        )
      );

      // Update selected event if it's the same report
      if (selectedEvent && selectedEvent.id === reportId) {
        setSelectedEvent(prev => ({
          ...prev,
          status: action === 'approve' ? 'approved' : 'rejected'
        }));
      }

      setApprovalNotes('');
      setApprovalLoading(false);
      
      // Show success message
      alert(`Report ${action}d successfully!`);
      
    } catch (err) {
      console.error(`Error ${action}ing report:`, err);
      setApprovalLoading(false);
      alert(`Failed to ${action} report: ${err.message}`);
    }
  };

  // Get color based on status
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending_review':
        return '#fff3cd'; // Light yellow for pending
      case 'approved':
        return '#d4edda'; // Light green for approved
      case 'rejected':
        return '#f8d7da'; // Light red for rejected
      case 'sent':
        return '#e6f4f1'; // Light teal for sent
      default:
        return '#e9ecef'; // Light gray for unknown status
    }
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = new Date(year, month, 1).getDay();
  
  // Filter reports for current month/year and map them to calendar events
  const calendarEvents = emailReports.filter(report => {
    const reportDate = new Date(report.sent_date || report.generated_at);
    return reportDate.getMonth() === month && reportDate.getFullYear() === year;
  }).map(report => ({
    id: report.id,
    date: report.sent_date || report.generated_at,
    subject: report.subject || report.subject_line,
    brand_name: report.brand_name,
    status: report.status,
    report_type: report.report_type || 'Performance Report'
  }));

  const eventMap = {};
  calendarEvents.forEach(e => {
    const day = new Date(e.date).getDate();
    if (!eventMap[day]) eventMap[day] = [];
    eventMap[day].push(e);
  });

  function prevMonth() {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  }
  
  function nextMonth() {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  }

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    fetchReportHtml(event.id);
  };

  if (loading) return <div style={{padding: '2rem', textAlign: 'center'}}>Loading email reports...</div>;
  if (error) return (
    <div style={{ padding: '2rem' }}>
      <DataFallback
        variant="pane"
        error={typeof error === 'string' ? { message: error, retryable: true } : error}
        onRetry={() => window.location.reload()}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      <button 
        onClick={() => navigate(-1)} 
        style={{
          marginBottom:'1.5rem',
          background:'#e6f4f1',
          color:'#004443',
          border:'none',
          borderRadius:6,
          padding:'0.5rem 1.2rem',
          fontWeight:600,
          cursor:'pointer'
        }}
      >
        ← Back
      </button>
      
      <h1 style={{marginBottom:'1.5rem',color:'#004443'}}>
        Performance Email Calendar
        {brandId && emailReports.length > 0 && (
          <span style={{fontSize:'1rem',fontWeight:'normal',marginLeft:'0.5rem'}}>
            - {emailReports[0]?.brand_name}
          </span>
        )}
      </h1>
      
      <div className={styles.calendarHeader}>
        <button onClick={prevMonth}>&lt;</button>
        <span style={{fontWeight:700,fontSize:'1.2rem'}}>
          {new Date(year, month).toLocaleString('default', { month: 'long' })} {year}
        </span>
        <button onClick={nextMonth}>&gt;</button>
      </div>
      
      {/* Status Legend */}
      <div style={{
        display:'flex',
        gap:'1rem',
        marginBottom:'1rem',
        padding:'0.5rem',
        background:'#f8f9fa',
        borderRadius:'8px',
        fontSize:'0.85rem'
      }}>
        <div style={{display:'flex',alignItems:'center',gap:'0.25rem'}}>
          <span>⏳</span>
          <span style={{color:'#856404'}}>Pending Review</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'0.25rem'}}>
          <span>✅</span>
          <span style={{color:'#155724'}}>Approved</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'0.25rem'}}>
          <span>❌</span>
          <span style={{color:'#721c24'}}>Rejected</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'0.25rem'}}>
          <span>📧</span>
          <span style={{color:'#495057'}}>Sent</span>
        </div>
      </div>
      
      <div className={styles.calendarGrid}>
        {[...Array(firstDay).keys()].map(i => <div key={'empty'+i} className={styles.emptyCell}></div>)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const events = eventMap[day] || [];
          return (
            <div key={day} className={styles.dayCell}>
              <div className={styles.dayNum}>{day}</div>
              {events.map(ev => (
                <div 
                  key={ev.id} 
                  className={styles.event} 
                  onClick={() => handleEventClick(ev)}
                  style={{
                    background: getStatusColor(ev.status),
                    cursor: 'pointer',
                    border: `2px solid ${ev.status === 'pending_review' ? '#ffc107' : ev.status === 'approved' ? '#28a745' : ev.status === 'rejected' ? '#dc3545' : '#6c757d'}`,
                    borderRadius: '4px',
                    margin: '2px 0'
                  }}
                >
                  <span role="img" aria-label="email">
                    {ev.status === 'pending_review' ? '⏳' : ev.status === 'approved' ? '✅' : ev.status === 'rejected' ? '❌' : '📧'}
                  </span> 
                  <div style={{fontSize:'0.8rem',lineHeight:'1.2'}}>
                    {ev.brand_name || 'Brand'}<br/>
                    <span style={{fontSize:'0.7rem',opacity:0.8}}>{ev.report_type}</span>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      
      {/* Modal for email content */}
      {selectedEvent && (
        <div className={styles.modalOverlay} onClick={() => {
          setSelectedEvent(null);
          setSelectedReportHtml(null);
          setApprovalNotes('');
        }}>
          <div className={styles.modalContent} style={{
            maxHeight:'90vh',
            maxWidth:'90vw',
            width:'800px',
            overflowY:'auto',
            padding:'2rem'
          }} onClick={e => e.stopPropagation()}>
            <button 
              className={styles.closeBtn} 
              onClick={() => {
                setSelectedEvent(null);
                setSelectedReportHtml(null);
                setApprovalNotes('');
              }}
              style={{
                position:'absolute',
                top:'10px',
                right:'15px',
                background:'#e6f4f1',
                color:'#004443',
                border:'none',
                borderRadius:'50%',
                width:'30px',
                height:'30px',
                cursor:'pointer',
                fontSize:'1.2rem'
              }}
            >
              ×
            </button>
            
            <h3 style={{marginBottom:'1rem',color:'#004443'}}>
              Email Report - {selectedEvent.brand_name || 'Brand'}
            </h3>
            
            <div style={{marginBottom:'1rem',fontSize:'0.9rem',color:'#666'}}>
              <strong>Type:</strong> {selectedEvent.report_type} | 
              <strong> Status:</strong> 
              <span style={{
                color: selectedEvent.status === 'pending_review' ? '#856404' : 
                       selectedEvent.status === 'approved' ? '#155724' : 
                       selectedEvent.status === 'rejected' ? '#721c24' : '#495057',
                fontWeight: 'bold',
                marginLeft: '4px'
              }}>
                {selectedEvent.status}
              </span> | 
              <strong> Generated:</strong> {new Date(selectedEvent.date).toLocaleDateString()}
            </div>
            
            {/* Approval/Disapproval controls for pending reports */}
            {selectedEvent.status === 'pending_review' && (
              <div style={{
                marginBottom:'1.5rem',
                padding:'1rem',
                background:'#fff3cd',
                border:'1px solid #ffeaa7',
                borderRadius:'8px'
              }}>
                <h4 style={{margin:'0 0 1rem 0',color:'#856404'}}>Review Required</h4>
                <div style={{marginBottom:'1rem'}}>
                  <label htmlFor="approvalNotes" style={{display:'block',marginBottom:'0.5rem',fontWeight:'bold'}}>
                    Notes (optional):
                  </label>
                  <textarea
                    id="approvalNotes"
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add review notes..."
                    style={{
                      width:'100%',
                      padding:'0.5rem',
                      border:'1px solid #ddd',
                      borderRadius:'4px',
                      minHeight:'60px',
                      resize:'vertical'
                    }}
                  />
                </div>
                <div style={{display:'flex',gap:'1rem'}}>
                  <button
                    onClick={() => handleApproval(selectedEvent.id, 'approve')}
                    disabled={approvalLoading}
                    style={{
                      background:'#28a745',
                      color:'white',
                      border:'none',
                      padding:'0.5rem 1rem',
                      borderRadius:'4px',
                      cursor: approvalLoading ? 'not-allowed' : 'pointer',
                      opacity: approvalLoading ? 0.6 : 1
                    }}
                  >
                    {approvalLoading ? 'Processing...' : '✅ Approve'}
                  </button>
                  <button
                    onClick={() => handleApproval(selectedEvent.id, 'disapprove')}
                    disabled={approvalLoading}
                    style={{
                      background:'#dc3545',
                      color:'white',
                      border:'none',
                      padding:'0.5rem 1rem',
                      borderRadius:'4px',
                      cursor: approvalLoading ? 'not-allowed' : 'pointer',
                      opacity: approvalLoading ? 0.6 : 1
                    }}
                  >
                    {approvalLoading ? 'Processing...' : '❌ Disapprove'}
                  </button>
                </div>
              </div>
            )}
            
            {htmlLoading ? (
              <div style={{textAlign:'center',padding:'2rem'}}>Loading email content...</div>
            ) : selectedReportHtml ? (
              <div 
                dangerouslySetInnerHTML={{__html: selectedReportHtml}}
                style={{
                  border:'1px solid #ddd',
                  borderRadius:'8px',
                  maxHeight:'60vh',
                  overflowY:'auto'
                }}
              />
            ) : (
              <div style={{textAlign:'center',padding:'2rem',color:'#888'}}>
                Failed to load email content
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandEmailCalendarPage;
